import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpErrorResponse,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { RequestService } from '../request.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  private readonly firstPartyBases = [
    environment.serverUrl,
    environment.serverLoginUrl,
    environment.scheduleUrl,
  ].filter(Boolean);

  constructor(
    private injector: Injector,
    private router: Router,
    private dialog: MatDialog
  ) {}

  private get requestService(): RequestService {
    return this.injector.get(RequestService);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const authReq = this.addAuthHeader(req);

    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 403 && this.isFirstPartyRequest(req)) {
          this.handleInvalidToken();
          return throwError(() => err);
        }

        if (err.status === 401 && this.shouldAttemptRefresh(req)) {
          return this.handleUnauthorized(authReq, next);
        }

        return throwError(() => err);
      })
    );
  }

  private isFirstPartyRequest(req: HttpRequest<any>): boolean {
    return this.firstPartyBases.some((base) => req.url.startsWith(base));
  }

  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    if (!this.isFirstPartyRequest(req)) {
      return req;
    }

    const token = this.requestService.getNewestTokenValue();
    if (token) {
      return req.clone({
        headers: req.headers.set('Authorization', token),
      });
    }
    return req;
  }

  private shouldAttemptRefresh(req: HttpRequest<any>): boolean {
    if (!this.isFirstPartyRequest(req)) {
      return false;
    }

    if (!this.requestService.getNewestTokenValue()) {
      return false;
    }

    const url = req.url.toLowerCase();
    const skipUrls = ['user/login', 'refreshtoken', 'forgotpassword', 'login/bytoken'];
    return !skipUrls.some((skipUrl) => url.includes(skipUrl));
  }

  private handleUnauthorized(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.requestService.refreshToken().pipe(
        switchMap((token) => {
          this.isRefreshing = false;

          if (!token) {
            this.handleInvalidToken();
            return throwError(() => new Error('Token refresh returned empty token'));
          }

          this.refreshTokenSubject.next(token);
          return next.handle(this.addAuthHeader(req));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.handleInvalidToken();
          return throwError(() => err);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap(() => next.handle(this.addAuthHeader(req)))
    );
  }

  private handleInvalidToken(): void {
    this.dialog.closeAll();
    this.requestService.logout(false);
    this.router.navigate(['/login']);
  }
}
